import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Euro, Truck, Box, Percent, Sparkles, UserPlus, CalendarPlus, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader, StatusBadge, EmptyState } from "../components/Shared";
import { api, eur, fmtDate } from "../lib/api";
import { MOVE_STATUS } from "../lib/labels";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/labels";

const Kpi = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="card-hover rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className="h-4 w-4 text-teal-500" />
    </div>
    <p className="mt-3 font-mono-num text-2xl font-bold text-[#0b2c6b]" data-testid={`${testid}-value`}>{value}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);

  return (
    <div className="fade-up">
      <PageHeader overline="Tableau de bord" title={`Bonjour ${user?.name?.split(" ")[0] || ""}`} subtitle="Vue d'ensemble de votre activité déménagement.">
        {can(user, "quotes") && <Button asChild className="bg-[#0b2c6b] hover:bg-[#1559d6]" data-testid="quick-new-quote"><Link to="/app/devis/nouveau"><Sparkles className="mr-2 h-4 w-4" />Nouveau devis IA</Link></Button>}
        {can(user, "clients") && <Button asChild variant="outline" data-testid="quick-add-client"><Link to="/app/clients"><UserPlus className="mr-2 h-4 w-4" />Client</Link></Button>}
        <Button asChild variant="outline" data-testid="quick-plan-move"><Link to="/app/planning"><CalendarPlus className="mr-2 h-4 w-4" />Planifier</Link></Button>
      </PageHeader>

      {d && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {d.revenue_month != null && <Kpi icon={Euro} label="CA facturé (mois)" value={eur(d.revenue_month)} hint={`Encaissé : ${eur(d.collected)}`} testid="kpi-revenue" />}
            <Kpi icon={Truck} label="Déménagements (mois)" value={d.moves_month} hint={`${d.vehicles_available}/${d.vehicles_total} véhicules dispo.`} testid="kpi-moves" />
            <Kpi icon={Box} label="Volume validé" value={`${d.volume_total} m³`} hint={`${d.quotes_pending} devis en attente`} testid="kpi-volume" />
            {d.conversion_rate != null && <Kpi icon={Percent} label="Transformation" value={`${d.conversion_rate} %`} hint={`Reste à encaisser : ${eur(d.outstanding)}`} testid="kpi-conversion" />}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid="dashboard-chart">
              <h3 className="mb-4 text-base font-semibold text-slate-900">Devis acceptés vs en attente (6 mois)</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.chart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="acceptes" name="Acceptés" fill="#0b2c6b" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="en_attente" name="En attente" fill="#10d9b5" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="dashboard-upcoming">
              <h3 className="mb-4 text-base font-semibold text-slate-900">Prochains déménagements</h3>
              {d.upcoming_moves.length === 0 ? (
                <EmptyState text="Aucun déménagement planifié" testid="upcoming-empty" />
              ) : (
                <ul className="space-y-3">
                  {d.upcoming_moves.map((m) => (
                    <li key={m.id} className="rounded-lg border border-slate-100 p-3" data-testid={`upcoming-move-${m.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900">{m.title}</p>
                        <StatusBadge map={MOVE_STATUS} value={m.status} />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{fmtDate(m.date)} · {m.start_time} · {m.volume_m3} m³</p>
                      <p className="mt-1 flex items-center gap-1 truncate text-xs text-slate-400"><MapPin className="h-3 w-3" />{m.origin} → {m.destination}</p>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-4 text-xs text-slate-500">Équipe disponible : {d.staff_available}/{d.staff_total} · Clients : {d.clients_count}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
